/* eslint-disable no-restricted-syntax */
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    List,
    ListItem,
    ListItemText,
} from '@material-ui/core';
import Loading from 'components/widgets/Loading';

const useStyles = makeStyles((theme) => ({
    listItem: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&.selected': {
            backgroundColor: theme.palette.action.selected,
        },
    },
    listItemText: {
        '& .MuiListItemText-primary': {
            fontSize: '0.875rem',
        },
    },
    field: {
        marginBottom: theme.spacing(0.5),
        display: 'flex',
        '& strong': {
            minWidth: 120,
            marginRight: theme.spacing(1),
            fontWeight: 600,
        },
    },
    listContainer: {
        height: '100%',
        overflow: 'auto',
        overflowX: 'hidden',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    emptyMessage: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
}));

const MobileListView = (props) => {
    const {
        columns,
        records,
        selectedRecord,
        onClickRecord,
        onSelectRow,
        isLoading,
        loadMore,
        totalCount,
        recordKeyField,
        isRecordSelected,
    } = props;

    const classes = useStyles();
    const listRef = useRef(null);

    const getRecordKey = (record, index) => {
        if (recordKeyField) {
            const keyValue = record[recordKeyField];
            if (keyValue !== undefined && keyValue !== null) {
                return keyValue;
            }
        }

        return record.id || index;
    };

    const checkIsRecordSelected = (record, selected) => {
        if (isRecordSelected) {
            return isRecordSelected(record, selected);
        }

        if (!selected) return false;

        if (recordKeyField) {
            const recordKey = record[recordKeyField];
            const selectedKey = selected[recordKeyField];

            return recordKey === selectedKey
                && recordKey !== undefined
                && recordKey !== null;
        }

        return record.id === selected.id;
    };

    useEffect(() => {
        if (!listRef.current || !loadMore) {
            return undefined;
        }

        const handleScroll = () => {
            const element = listRef.current;
            if (!element) return;

            const currentScrollPosition = Math.ceil(element.offsetHeight + element.scrollTop);
            const maximumScrollPosition = element.scrollHeight;

            if (
                currentScrollPosition >= maximumScrollPosition - 15
                && records.length < totalCount
                && !isLoading
            ) {
                loadMore();
            }
        };

        const element = listRef.current;
        element.addEventListener('scroll', handleScroll);

        return () => {
            if (element) {
                element.removeEventListener('scroll', handleScroll);
            }
        };
    }, [loadMore, records.length, totalCount, isLoading]);

    const getFieldValue = (record, accessor) => {
        if (!accessor) return '-';

        if (typeof accessor === 'function') {
            return accessor(record) || '-';
        }

        const keys = accessor.split('.');
        let value = record;

        for (const key of keys) {
            value = value?.[key];
            if (value === undefined || value === null) {
                return '-';
            }
        }

        return value || '-';
    };

    const renderAllFields = (record) => (
        <div>
            {columns.map((column, index) => (
                <div key={index} className={classes.field}>
                    <strong>
                        {column.Header}
                        :
                    </strong>
                    <span>
                        {
                            column.renderCustomCell
                                ? column.Cell({ value: getFieldValue(record, column.accessor) })
                                : getFieldValue(record, column.accessor)
                        }
                    </span>
                </div>
            ))}
        </div>
    );

    return (
        <div className={classes.listContainer} ref={listRef}>
            {records.length === 0 && !isLoading ? (
                <div className={classes.emptyMessage}>
                    No rows found
                </div>
            ) : (
                <List>
                    {records.map((record, index) => {
                        const isSelected = checkIsRecordSelected(record, selectedRecord);
                        const key = getRecordKey(record, index);

                        return (
                            <ListItem
                                key={key}
                                className={`${classes.listItem} ${isSelected ? 'selected' : ''}`}
                                onClick={() => onClickRecord?.(record)}
                                onDoubleClick={() => onSelectRow(record)}
                            >
                                <ListItemText
                                    className={classes.listItemText}
                                    primary={renderAllFields(record)}
                                    primaryTypographyProps={{ component: 'div' }}
                                />
                            </ListItem>
                        );
                    })}
                    {isLoading && records.length > 0 && (
                        <div className={classes.loadingContainer}>
                            <Loading className="loading-table" />
                        </div>
                    )}
                </List>
            )}
        </div>
    );
};

MobileListView.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        Header: PropTypes.string.isRequired,
        accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    })).isRequired,
    records: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedRecord: PropTypes.object,
    onClickRecord: PropTypes.func,
    onSelectRow: PropTypes.func,
    isLoading: PropTypes.bool,
    loadMore: PropTypes.func,
    totalCount: PropTypes.number,
    recordKeyField: PropTypes.string,
    isRecordSelected: PropTypes.func,
};

MobileListView.defaultProps = {
    selectedRecord: null,
    isLoading: false,
    loadMore: null,
    totalCount: 0,
    recordKeyField: null,
    isRecordSelected: null,
    onClickRecord: () => {},
    onSelectRow: () => {},
};

export default MobileListView;
